import { WeekDay } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatCalendarCellCssClasses, MatCalendarView } from '@angular/material/datepicker';

import { SharedCssClassNames } from '@app/eds';
import { TranslateService } from '@ngx-translate/core';

export type specificDateClassType = {
  className: string;
  date: Date;
};

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss']
})
export class DatepickerComponent implements OnInit {
  // Whether to show datepicker component or not
  closeDatepicker?: boolean;

  // Classname for the mat calendar when year and month view is opened
  classNames = SharedCssClassNames;

  // Whether to have the provision to close the date picker component
  // on outside/not
  @Input() shouldCloseModalOnOutsideClick = false;

  // The minimum selectable(by default it will be today's date).
  @Input() minDate: Date | null = new Date();

  // The maximum selectable(by default it will be today's date).
  @Input() maxDate!: Date | null;

  // The currently selected date.
  @Input() selected!: Date | null;

  // tells should disable weekends or not
  @Input() isWeekendDisabled!: boolean;

  // Function used to filter which dates are selectable
  @Input() dateFilter!: (date: Date) => boolean;

  // Boolean to specify if the current date needs to be highlighted
  @Input() isCurrentDateHighlighted = false;

  // Will get list of specificDate classes, used to add custom class
  @Input() specificDateClassList: specificDateClassType[] = [];

  // Emits when currently selected date changes.
  @Output() selectedChange: EventEmitter<Date | null> = new EventEmitter<Date | null>();

  // Emits when calendar view is changed.
  @Output() viewChange: EventEmitter<MatCalendarView | null> =
    new EventEmitter<MatCalendarView | null>();

  // Emits event when outside of calendar is clicked
  @Output() closedCalendarEvent: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly translate: TranslateService
  ) {
    //this.dateAdapter.setLocale(this.translate.instant('datepicker.languageCode'));
  }

  ngOnInit() {
    // disables weekend if isWeekendDisabled is true
    if (this.isWeekendDisabled) {
      this.dateFilter = this.workDaysFilter;
    }
  }

  /**
   * Checks if the clicked portion is outside calendar so will set closeDatepicker to true
   * and emit event closedCalendarEvent, if the calendar is opened for first time then
   * will set closeDatepicker to false. If we do not want this feature to auto close on outside
   * click then it will just return and no event will be emitted.
   */
  closeCalendar = () => {
    if (!this.shouldCloseModalOnOutsideClick) {
      return;
    }

    if (this.closeDatepicker !== undefined) {
      this.closeDatepicker = true;
      this.closedCalendarEvent.emit();
    } else {
      this.closeDatepicker = false;
    }
  };

  /**
   * This filter allows working days(mon-fri) as selectable dates.
   *
   * @param { Date } date takes date
   * @returns { boolean } whether date is selectable or not.
   */
  workDaysFilter = (date: Date): boolean => {
    const day: number = date.getDay();
    return day !== WeekDay.Sunday && day !== WeekDay.Saturday;
  };

  /**
   * This emits selected date to parent component to use.
   */
  dateSelected = (): void => this.selectedChange.emit(this.selected);

  /**
   * This Function will add a custom class to a specific date.
   * Will compare date in local time only.
   *
   * @returns {Function} MatCalendarCellCssClasses
   */
  getSpecificClassForParticularDate = () => {
    return (date: Date): MatCalendarCellCssClasses => {
      for (const item of this.specificDateClassList) {
        if (date.toDateString() === item.date.toDateString()) {
          return item.className;
        }
      }
      return '';
    };
  };

  /**
   * This emits calendar view to parent component.
   *
   * @param {MatCalendarView} view
   */
  getCalendarView = (view: MatCalendarView) => this.viewChange.emit(view);
}
